import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Gamepad2, Search, Download, Plus, RefreshCw, Eye, Loader2, ChevronLeft, ChevronRight, Undo2, FolderInput, Link, Rocket } from 'lucide-react';
import { toast } from 'sonner';
import { getAllPlayables, updatePlayable, deletePlayable, attachPlayable, buildShareUrl, type PlayableListItem, type PlayablePagination } from '@/api/modules/playables';
import { createBatch } from '@/api/modules/batch';
import { cn } from '@/utils/cn';
import { CreateBatchDialog } from '@/components/CreateBatchDialog';
import { PublishToPlatformDialog } from '@/components/publish-to-platform-dialog';
import { useConfirm } from '@/components/ui/confirm-dialog';
import { Select } from '@/components/ui/select';
import { ProjectSelectDialog } from '@/components/ProjectSelectDialog';

const GENERATION_METHOD_LABELS: Record<string, string> = {
  manual: '手动生成',
  variant: '批量变体',
};

const GENERATION_MODE_LABELS: Record<string, string> = {
  'ai-chat': 'AI生成',
  'batch-variant': '批量变体',
  'template': '内置模板',
  'blueprint': '蓝图模式',
  'video': '视频转试玩',
  'cocos-source': 'Cocos源码',
  // BUG-20260807-PW-003 兼容 GENERATION_MODE_OPTIONS 的 'cocos' 值
  'cocos': 'Cocos源码',
  'cocos-generation': 'Cocos生成',
};

function formatFileSize(bytes: number | null): string {
  if (!bytes) return '-';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function deriveGenerationMethod(mode?: string | null): 'manual' | 'variant' {
  return mode === 'batch-variant' ? 'variant' : 'manual';
}

export function PlayableCenter(): JSX.Element {
  const navigate = useNavigate();
  const [playables, setPlayables] = useState<PlayableListItem[]>([]);
  const [pagination, setPagination] = useState<PlayablePagination>({ page: 1, limit: 20, total: 0, totalPages: 0 });
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [filterMode, setFilterMode] = useState('');
  const [filterMethod, setFilterMethod] = useState('');
  const [showBatchDialog, setShowBatchDialog] = useState(false);
  const [batchPlayableId, setBatchPlayableId] = useState<string | undefined>(undefined);
  // 「保存到项目」：当前待挂靠项目的试玩（sandbox 试玩挂载入口）
  const [attachTarget, setAttachTarget] = useState<PlayableListItem | null>(null);
  // 「发布到投放平台」：当前待发布的试玩（功能暂未支持，点击仅弹提示）
  const [platformTarget, setPlatformTarget] = useState<PlayableListItem | null>(null);

  const fetchPlayables = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const res = await getAllPlayables({
        page,
        limit: 20,
        generationMode: filterMode || undefined,
        generationMethod: (filterMethod || undefined) as 'manual' | 'variant' | undefined,
        status: 'published',
        search: search || undefined,
      });
      setPlayables(res.data);
      setPagination(res.pagination);
    } catch {
      toast.error('加载试玩列表失败');
    } finally {
      setLoading(false);
    }
  }, [filterMode, filterMethod, search]);

  useEffect(() => { fetchPlayables(); }, [fetchPlayables]);

  const { confirm, dialog } = useConfirm();

  const handleDelete = (id: string) => {
    confirm('删除试玩', '确定要删除这个试玩吗？删除后无法恢复。', () => {
      deletePlayable(id).then(() => {
        toast.success('已删除');
        fetchPlayables(pagination.page);
      }).catch(() => toast.error('删除失败'));
    });
  };

  /** 撤回已发布试玩：status published → pending，从资产列表移除（不联动投放平台） */
  const handleUnpublish = (p: PlayableListItem) => {
    // 已投放过的试玩：明确告知投放平台素材不受影响（平台无删除接口，撤回无法下架）
    const platformNote = (p.platformPublishes?.length ?? 0) > 0
      ? '注意：该试玩已发布到投放平台，撤回仅从本平台列表移除，投放平台上的素材不受影响（投放平台无删除接口，如需下架请前往投放平台操作）。'
      : '';
    confirm('撤回试玩', `确定撤回「${p.variantLabel || `V${p.version}`}」吗？撤回后将从已发布列表中移除，可重新发布。${platformNote}`, () => {
      updatePlayable(p.id, { status: 'pending' }).then(() => {
        toast.success('已撤回');
        fetchPlayables(pagination.page);
      }).catch(() => toast.error('撤回失败，请重试'));
    });
  };

  /** 保存到项目：sandbox 试玩挂载到所选项目（后端只改 DB 归属，不移动 OSS 对象） */
  const handleAttachConfirm = async (projectId: string) => {
    if (!attachTarget) return;
    try {
      const res = await attachPlayable(attachTarget.id, projectId);
      if (!res?.success) throw new Error(res?.message || 'attach failed');
      toast.success('已保存到项目');
      setAttachTarget(null);
      fetchPlayables(pagination.page);
    } catch (err: any) {
      toast.error(`保存失败：${err?.message ?? '请重试'}`);
      throw err; // 抛出以保持弹窗打开便于重试
    }
  };

  /** 复制分享链接：一律用 /api/p/:name 短链（与 OSS 物理路径解耦），无分享 ID 时退回预览直链 */
  const handleCopyShareUrl = (p: PlayableListItem) => {
    const url = buildShareUrl(p.name) ?? p.previewUrl;
    if (!url) return;
    navigator.clipboard.writeText(url);
    toast.success('已复制分享链接');
  };

  const handleBatchCreate = async (desc: string, count: number, sourcePlayableId?: string, projectId?: string, dimensions?: Array<{ name: string; values: string[] }>) => {
    try {
      await createBatch({ description: desc, count, sourcePlayableId, projectId, dimensions });
      toast.success('已创建批量变体任务', {
        description: '进度和生成结果请前往「创意工厂」查看',
        action: {
          label: '前往创意工厂',
          onClick: () => navigate('/batch'),
        },
      });
      setShowBatchDialog(false);
      setBatchPlayableId(undefined);
    } catch {
      toast.error('创建失败，请确认后端服务已启动');
    }
  };

  return (
    <div className="flex flex-1 flex-col">
      {/* Header */}
      <div className="page-header">
        <div>
          <h2 className="page-title" style={{ animation: 'none' }}>
            <Gamepad2 size={18} className="shrink-0 text-neon-cyan" />
            试玩列表
          </h2>
        </div>
        <button onClick={() => fetchPlayables(pagination.page)} disabled={loading}
          className="icon-btn flex items-center gap-1.5 rounded-xl px-3 py-2 text-sm">
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />刷新
        </button>
      </div>

      {/* Filters */}
      <div className="mb-4 flex items-center gap-2 flex-wrap">
        <div className="relative w-[260px]">
          <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input className="input-neon w-full py-2 pl-8 pr-3 text-sm" placeholder="搜索试玩名称..."
            value={search} onChange={e => setSearch(e.target.value)}
            onKeyDown={e => { if (e.key === 'Enter') fetchPlayables(1); }} />
        </div>
        <Select
          options={[
            { value: '', label: '全部创建方式' },
            { value: 'manual', label: '手动生成' },
            { value: 'variant', label: '批量变体' },
          ]}
          value={filterMethod}
          onChange={(v) => { setFilterMethod(v); fetchPlayables(1); }}
          className="input-neon py-2 px-3 text-sm"
        />
        <Select
          options={[
            { value: '', label: '全部构建模式' },
            { value: 'template', label: '内置模板' },
            { value: 'blueprint', label: '蓝图模式' },
            { value: 'cocos-source', label: 'Cocos源码' },
            { value: 'video', label: '视频转试玩' },
          ]}
          value={filterMode}
          onChange={setFilterMode}
          className="input-neon py-2 px-3 text-sm"
        />
        <span className="text-xs text-muted-foreground">共 {pagination.total} 个试玩</span>
      </div>

      {/* Table */}
      {loading ? (
        <div className="flex flex-1 flex-col items-center justify-start gap-3 pt-28">
          <Loader2 size={32} className="animate-spin text-neon-cyan" />
          <span className="text-xs text-muted-foreground">加载中...</span>
        </div>
      ) : playables.length === 0 ? (
        <div className="flex flex-1 flex-col items-center justify-center gap-3 py-20">
          <Gamepad2 size={40} className="text-muted-foreground/40" />
          <p className="text-sm text-muted-foreground">暂无试玩记录</p>
          <p className="text-xs text-muted-foreground/70">通过 AI 工作台生成试玩后，会在此处展示</p>
        </div>
      ) : (
        <div className="flex flex-col gap-2 stagger-children">
          {/* Table header */}
          <div className="glass-panel hidden grid-cols-12 items-center gap-3 px-4 py-2.5 text-[11px] font-medium text-muted-foreground md:grid">
            <span className="col-span-3">试玩</span>
            <span className="col-span-1 text-center">创建方式</span>
            <span className="col-span-1 text-center">构建模式</span>
            <span className="col-span-1 text-center">版本</span>
            <span className="col-span-1 text-center">评分</span>
            <span className="col-span-1 text-center">大小</span>
            <span className="col-span-2 text-center">创建时间</span>
            <span className="col-span-2 text-right">操作</span>
          </div>

          {playables.map((p) => {
            const score = p.quality?.score;
            // 创建方式以后端判定为准（generationMethod），旧数据兜底按 generationMode 推导
            const method = p.generationMethod ?? deriveGenerationMethod(p.generationMode);
            const methodLabel = GENERATION_METHOD_LABELS[method] ?? method;
            const modeLabel = p.generationMode?.trim() ? GENERATION_MODE_LABELS[p.generationMode.trim()] ?? p.generationMode : '未知';
            const methodColor = method === 'variant' ? 'text-neon-purple bg-neon-purple/15' : 'bg-muted/40 text-foreground/70';
            const modeColor = 'bg-muted/40 text-foreground/70';
            return (
              <div key={p.id}
                className="glass-card group flex flex-col gap-3 p-4 md:grid md:grid-cols-12 md:items-center md:gap-3 md:p-3">
                {/* Name + label */}
                <div className="col-span-3 flex flex-col gap-0.5">
                  <span className="truncate text-sm font-medium text-foreground">
                    {p.variantLabel || `V${p.version}`}
                  </span>
                  {p.name && <code className="text-[10px] text-neon-cyan">{p.name}</code>}
                  {(p.platformPublishes?.length ?? 0) > 0 && (
                    <div className="flex flex-wrap items-center gap-1">
                      {Array.from(new Set(p.platformPublishes!.map(pub => pub.env))).map(env => (
                        <span key={env} className={cn('rounded px-1.5 py-0.5 text-[10px]', env === 'prod' ? 'bg-neon-cyan/15 text-neon-cyan' : 'bg-muted/40 text-foreground/70')}>
                          {env === 'prod' ? '已投放·线上' : '已投放·测试'}
                        </span>
                      ))}
                    </div>
                  )}
                  {p.projectId ? (
                    <span className="truncate text-[10px] text-muted-foreground">{p.projectName || '已挂靠项目'}</span>
                  ) : (
                    <span className="text-[10px] text-amber-400/80">未挂靠项目</span>
                  )}
                </div>

                {/* Generation Method */}
                <div className="col-span-1 flex items-center justify-center">
                  <span className={cn('rounded px-1.5 py-0.5 text-[10px]', methodColor)}>{methodLabel}</span>
                </div>

                {/* Generation Mode */}
                <div className="col-span-1 flex items-center justify-center">
                  <span className={cn('rounded px-1.5 py-0.5 text-[10px]', modeColor)}>{modeLabel}</span>
                </div>

                {/* Version */}
                <div className="col-span-1 text-center text-xs text-foreground/80">V{p.version}</div>

                {/* Score */}
                <div className="col-span-1 text-center">
                  {typeof score === 'number' ? (
                    <span className={cn('text-xs font-medium', score >= 90 ? 'text-neon-green' : score >= 70 ? 'text-neon-cyan' : 'text-amber-400')}>
                      {score}
                    </span>
                  ) : <span className="text-xs text-muted-foreground">-</span>}
                </div>

                {/* Size */}
                <div className="col-span-1 text-center text-[11px] text-muted-foreground">{formatFileSize(p.fileSize)}</div>

                {/* Created */}
                <div className="col-span-2 text-center text-[11px] text-muted-foreground">
                  {new Date(p.createdAt).toLocaleString('zh-CN', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                </div>

                {/* Actions */}
                <div className="col-span-2 flex items-center justify-end gap-1">
                  {p.previewUrl && (
                    <button onClick={() => window.open(p.previewUrl!, '_blank')} className="icon-btn rounded p-1.5" title="预览">
                      <Eye size={12} />
                    </button>
                  )}
                  {(p.name || p.previewUrl) && (
                    <button onClick={() => handleCopyShareUrl(p)} className="icon-btn rounded p-1.5" title="复制分享链接">
                      <Link size={12} />
                    </button>
                  )}
                  {!p.projectId && (
                    <button onClick={() => setAttachTarget(p)} className="icon-btn rounded p-1.5 text-neon-cyan/90 hover:bg-neon-cyan/10" title="保存到项目">
                      <FolderInput size={12} />
                    </button>
                  )}
                  {p.previewUrl && (
                    <button onClick={() => { window.open(`/api/playable/download/${p.id}`, '_blank'); }} className="icon-btn rounded p-1.5" title="下载">
                      <Download size={12} />
                    </button>
                  )}
                  <button onClick={() => { setBatchPlayableId(p.id); setShowBatchDialog(true); }} className="icon-btn rounded p-1.5" title="创建批量任务">
                    <Plus size={12} />
                  </button>
                  <button onClick={() => setPlatformTarget(p)} className="icon-btn rounded p-1.5 text-neon-purple/90 hover:bg-neon-purple/10" title="发布到投放平台">
                    <Rocket size={12} />
                  </button>
                  <button onClick={() => handleUnpublish(p)} className="icon-btn rounded p-1.5 text-amber-500/90 hover:bg-amber-500/10" title="撤回发布">
                    <Undo2 size={12} />
                  </button>
                </div>
              </div>
            );
          })}

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 pt-2">
              <button onClick={() => fetchPlayables(pagination.page - 1)} disabled={pagination.page <= 1}
                className="icon-btn rounded p-1.5 disabled:opacity-30"><ChevronLeft size={14} /></button>
              <span className="text-xs text-muted-foreground">{pagination.page} / {pagination.totalPages}</span>
              <button onClick={() => fetchPlayables(pagination.page + 1)} disabled={pagination.page >= pagination.totalPages}
                className="icon-btn rounded p-1.5 disabled:opacity-30"><ChevronRight size={14} /></button>
            </div>
          )}
        </div>
      )}

      <CreateBatchDialog
        open={showBatchDialog}
        onClose={() => { setShowBatchDialog(false); setBatchPlayableId(undefined); }}
        onCreate={handleBatchCreate}
        initialPlayableId={batchPlayableId}
      />
      {dialog}
      <PublishToPlatformDialog open={!!platformTarget} onClose={() => setPlatformTarget(null)} />
      <ProjectSelectDialog
        open={!!attachTarget}
        onClose={() => setAttachTarget(null)}
        onConfirm={handleAttachConfirm}
        playableLabel={attachTarget?.variantLabel || (attachTarget ? `V${attachTarget.version}` : undefined)}
      />
    </div>
  );
}
